import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/lib/api-error';
import type { CampoTemplate, PlantillaParametro, TemplateCatalog, WhatsappTemplate } from '@/types/templates';
import { campoEtiqueta, describeEncabezado, describeIdentidad, describeParametros } from './templates-format';
import { previewPayload, valoresEjemplo } from './templates-payload';
import { valorFieldKey, TEMPLATE_LIMITS } from './templates-validation';
import { usePreviewTemplate } from './use-templates';

/**
 * Previsualización del cuerpo renderizado (`POST .../:templateId/preview`).
 *
 * Es la forma de ver EXACTAMENTE qué recibe el cliente antes de mandar la plantilla a Meta: el backend
 * reemplaza cada `{{n}}` con el valor del campo mapeado (los que se escriban acá o, si falta alguno, el
 * `ejemplo` del catálogo) y devuelve el texto final más los valores POSICIONALES en el orden en que los
 * manda Meta.
 *
 * Lo puede abrir cualquier miembro del negocio: el endpoint no escribe nada. Cualquier desajuste entre
 * el mapeo y las variables del cuerpo lo reporta el backend con un 400 (el mismo criterio que al
 * guardar), así que este diálogo también sirve para detectarlo.
 */

export interface TemplatePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  /** Plantilla que se previsualiza (`null` = el diálogo no se pinta). */
  template: WhatsappTemplate | null;
  /** Catálogo de eventos: de ahí salen los campos mapeados y sus ejemplos. */
  catalogo: TemplateCatalog | null;
}

/** Campos del catálogo que la plantilla usa de verdad (los que aparecen en su mapeo). */
function camposUsados(campos: CampoTemplate[], parametros: PlantillaParametro[]): CampoTemplate[] {
  const usados: CampoTemplate[] = [];

  for (const parametro of parametros) {
    const campo = campos.find((candidato) => candidato.clave === parametro.campo);
    if (campo && !usados.includes(campo)) usados.push(campo);
  }

  return usados;
}

export function TemplatePreviewDialog({
  open,
  onClose,
  tenantId,
  template,
  catalogo,
}: TemplatePreviewDialogProps) {
  const { mutate: previsualizar, data, isPending, error } = usePreviewTemplate(tenantId);

  const [valores, setValores] = useState<Record<string, string>>({});

  const def =
    template && catalogo
      ? (catalogo.eventos.find((evento) => evento.evento === template.evento) ?? null)
      : null;

  /** Cada apertura arranca con los valores de ejemplo del catálogo y pide la previsualización. */
  useEffect(() => {
    if (!open || !template || !def) return;

    const iniciales = valoresEjemplo(camposUsados(def.campos, template.parametros));
    setValores(iniciales);
    previsualizar({ templateId: template.id, payload: previewPayload(iniciales) });
  }, [open, template, def, previsualizar]);

  const campos = def ? camposUsados(def.campos, template?.parametros ?? []) : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Previsualizar la plantilla"
      description={
        template
          ? `${describeIdentidad(template)} · ${describeEncabezado(template)}`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cerrar
          </Button>
          <Button
            loading={isPending}
            disabled={!template}
            onClick={() =>
              template && previsualizar({ templateId: template.id, payload: previewPayload(valores) })
            }
          >
            Actualizar la previsualización
          </Button>
        </>
      }
    >
      {!template || !def ? (
        <PageLoader label="Cargando la plantilla…" />
      ) : (
        <div className="space-y-5">
          {error ? <Alert>{getErrorMessage(error)}</Alert> : null}

          {campos.length > 0 ? (
            <div className="space-y-4">
              <p className="text-caption text-ink-muted">
                Escribe los valores para ver el texto con datos de verdad (los que queden vacíos usan el
                ejemplo del catálogo).
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                {campos.map((campo) => (
                  <Field
                    key={campo.clave}
                    label={campoEtiqueta(def.campos, campo.clave)}
                    htmlFor={`preview-${campo.clave}`}
                    hint={`Ejemplo: ${campo.ejemplo}`}
                  >
                    <Input
                      id={`preview-${campo.clave}`}
                      name={valorFieldKey(campo.clave)}
                      value={valores[campo.clave] ?? ''}
                      maxLength={TEMPLATE_LIMITS.valor}
                      onChange={(event) =>
                        setValores((current) => ({ ...current, [campo.clave]: event.target.value }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-caption text-ink-muted">
              Esta plantilla no tiene variables mapeadas: el cuerpo se manda tal cual.
            </p>
          )}

          {data ? (
            <div className="space-y-4">
              <div className="rounded-panel border border-line bg-surface-muted px-3.5 py-3">
                <p className="text-micro font-semibold tracking-wide text-ink-faint uppercase">
                  Así lo recibe el cliente
                </p>

                <div className="mt-2 max-w-md rounded-card border border-line bg-surface px-3.5 py-3 shadow-card">
                  <p className="whitespace-pre-wrap text-body text-ink">{data.cuerpoRenderizado}</p>
                  {data.pie ? (
                    <p className="mt-2 text-micro text-ink-faint">{data.pie}</p>
                  ) : null}
                </div>

                <p className="mt-3 text-caption text-ink-muted">
                  {describeParametros(data.parametros, data.campos)}
                </p>
              </div>

              {data.parametrosResueltos.length > 0 ? (
                <div>
                  <p className="text-caption font-medium text-ink-soft">
                    Valores posicionales que Meta recibe, en orden
                  </p>
                  <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-caption text-ink-muted">
                    {data.parametrosResueltos.map((valor, index) => (
                      <li key={`${index}-${valor}`}>{valor}</li>
                    ))}
                  </ol>
                </div>
              ) : null}

              <Alert tone="info">
                Este texto es el de la plantilla APROBADA en Meta: si algo no te gusta, corrígelo en
                WhatsApp Manager (este módulo no propaga ediciones a Meta) o edítalo acá mientras siga en
                BORRADOR.
              </Alert>
            </div>
          ) : null}

        </div>
      )}
    </Modal>
  );
}
